import { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { MessageSquare, Send, X, Sparkles, Brain, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';

const MOCK_RESPONSES = {
  'metformin': 'Maximum dose of Metformin in CKD:\n\u2022 eGFR \u226545: Up to 2000mg/day (no adjustment)\n\u2022 eGFR 30-44: Max 1000mg/day\n\u2022 eGFR <30: Contraindicated\n\nFor this patient (eGFR 72), full dose is safe.',
  'bp target': 'BP targets for patients with diabetes:\n\u2022 ADA 2026: <130/80 mmHg\n\u2022 Current reading: 138/88 \u2014 above target\n\u2022 Consider intensifying Lisinopril or adding amlodipine',
  'hba1c': 'HbA1c interpretation:\n\u2022 Current: 7.2% (above target of <7.0%)\n\u2022 Last 3 values: 7.2%, 7.4%, 7.5% (trending down)\n\u2022 Suggest: Continue current regimen, recheck in 3 months',
  'default': "I can help with clinical questions about your current patient. Try asking about medications, lab values, treatment guidelines, or drug interactions.",
};

const getResponse = (q) => {
  const lower = q.toLowerCase();
  if (lower.includes('metformin') || lower.includes('max dose') || lower.includes('ckd')) return MOCK_RESPONSES.metformin;
  if (lower.includes('bp') || lower.includes('blood pressure') || lower.includes('target')) return MOCK_RESPONSES['bp target'];
  if (lower.includes('a1c') || lower.includes('hba1c') || lower.includes('hemoglobin')) return MOCK_RESPONSES.hba1c;
  return MOCK_RESPONSES.default;
};

const LuminaChatbot = ({ patient, transcript }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const sendMessage = () => {
    if (!input.trim()) return;
    const userMsg = { role: 'user', text: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
    setTimeout(() => {
      const resp = getResponse(userMsg.text);
      setMessages(prev => [...prev, { role: 'assistant', text: resp }]);
      setIsTyping(false);
    }, 800);
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button onClick={() => setIsOpen(true)}
          className="fixed bottom-[70px] right-6 h-12 w-12 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 flex items-center justify-center transition-all hover:scale-105 z-[9999]"
          data-testid="chatbot-fab">
          <MessageSquare className="h-5 w-5" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-[70px] right-6 w-80 h-[420px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden z-[9999]" data-testid="chatbot-panel">
          {/* Header */}
          <div className="bg-slate-900 px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-violet-600/30 flex items-center justify-center">
                <Brain className="h-3.5 w-3.5 text-violet-300" />
              </div>
              <div>
                <p className="text-xs font-bold text-white">Lumina AI</p>
                {patient && <p className="text-[9px] text-slate-400">Context: {patient.firstName} {patient.lastName}</p>}
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white transition-colors" data-testid="chatbot-close">
              <Minimize2 className="h-4 w-4" />
            </button>
          </div>

          {/* Context Banner */}
          {patient && (
            <div className="px-3 py-1.5 bg-violet-50 border-b border-violet-100 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-violet-500" />
              <p className="text-[9px] text-violet-600 font-medium">
                Context-aware: {patient.firstName}'s chart, {patient.medications?.length || 0} meds, {transcript?.length || 0} transcript lines
              </p>
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="chatbot-messages">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <Brain className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-xs text-slate-400 font-medium">Ask a clinical question</p>
                <p className="text-[10px] text-slate-300 mt-1">I know your patient's chart and current conversation</p>
                <div className="mt-3 space-y-1">
                  {["What's the max dose of Metformin in CKD?", "What's the BP target for this patient?", "Summarize today's HbA1c trend"].map((q, i) => (
                    <button key={i} onClick={() => { setInput(q); }}
                      className="block w-full text-left px-3 py-1.5 rounded-lg bg-slate-50 text-[10px] text-slate-500 hover:bg-slate-100 transition-colors"
                      data-testid={`suggestion-q-${i}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={cn("flex", msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div className={cn("max-w-[85%] rounded-xl px-3 py-2 text-xs",
                  msg.role === 'user' ? 'bg-slate-900 text-white rounded-br-sm' : 'bg-slate-100 text-slate-700 rounded-bl-sm')}>
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-xl px-3 py-2 rounded-bl-sm">
                  <div className="flex gap-1">
                    <div className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-100 bg-white shrink-0">
            <div className="flex gap-2">
              <Input value={input} onChange={e => setInput(e.target.value)} placeholder="Ask a clinical question..."
                className="text-xs h-8" onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }} data-testid="chatbot-input" />
              <Button size="sm" className="h-8 w-8 p-0 bg-slate-900" onClick={sendMessage} disabled={!input.trim()} data-testid="chatbot-send">
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LuminaChatbot;
